      import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Observable, Subject, filter, takeUntil } from 'rxjs';
import Icons from 'src/app/core/enums/icons.enum';
import { UserService } from 'src/app/core/services/user.service';
import { SiteModel } from 'src/app/core/models/sites.model';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserLoginModel } from 'src/app/core/models/user-login.model';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { TemplatePortal } from '@angular/cdk/portal';
import { Util } from 'src/app/core/shared/util';
import { ToastMessageService } from 'src/app/core/services/toast-message.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { AuthenticationEvent } from 'src/app/core/models/authentication-event.model';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import AuthType from 'src/app/core/enums/auth.type.enum';
import { Application } from 'src/portaljs/constants/application';
import { ScreenStateService } from 'src/app/core/services/screen-state.service';
import { ScreenState } from 'src/app/core/enums/sceen-state.enum';

@Component({
    selector: 'fss-portal-site-switcher-menu',
    templateUrl: './site-switcher-menu.component.html',
    styleUrls: ['./site-switcher-menu.component.scss'],
    standalone: false
})
export class SiteSwitcherMenuComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  public icons = Icons;
  private availableSites: SiteModel[] = [];
  private districtUserType = false;
  public searchText: string = '';
  public enrolledSites: SiteModel[] = [];
  public enrolledSites$: Observable<SiteModel[]> = this.userService.getEnrolledSitesInDistrict$();
  public filteredSites: SiteModel[] = [];
  public portal: TemplatePortal<any>;
  public isGuest: boolean = false;
  public isShared: boolean = false;
  public readonly MOBILE_HEADER_TEXT = 'Switch Sites';

  @ViewChild('siteSwitchTrigger') public siteSwitchTrigger: MatMenuTrigger;
  @ViewChild('siteSwitcherTemplate') public siteSwitcherTemplate: TemplateRef<any>;
  @ViewChild('schoolFilterInput') public schoolFilterInput: ElementRef;

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  public get enrolledSiteCount(): number {
    return this.enrolledSites.length;
  }

  public get siteSwitcherOptionCount(): number {
    if (this.districtUserType) {
      return this.enrolledSiteCount;
    } else {
      //site users have enrolledSites - 1 (current site) options in the site switcher
      return this.enrolledSiteCount - 1;
    }
  }

  public isShowingSiteSwitcher(): boolean {
    if (this.authService.getUserLogin()) {
      return true;
    }
    return false;
  }

  public get currentSite(): SiteModel {
    const siteGuid = Util.getSiteGuidFromContext(this.authService.getUserLogin()?.getSiteGuid());
    return this.enrolledSites.find(({ guid }) => guid === siteGuid);
  }

  constructor(
    private screenStateService: ScreenStateService,
    private userService: UserService,
    private readonly toolbarService: ToolbarService,
    private authService: AuthService,
    private portalStateService: PortalStateService,
    private toastService: ToastMessageService,
    private router: Router,
    private route: ActivatedRoute,
    private _viewContainerRef: ViewContainerRef) {
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    this.screenStateService.screenState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        if (this.isUnEnabledForCurrentApp()) {
          if (this.siteSwitchTrigger && this.siteSwitchTrigger.menuOpen && state === ScreenState.Mobile) {
            this.siteSwitchTrigger.closeMenu();
            this.showInSidenav();
          }
          if (this.portal && state !== ScreenState.Mobile && this.toolbarService?.portal?.title === this.MOBILE_HEADER_TEXT) {
            this.portal = undefined;
            this.toolbarService.portal = undefined;
            setTimeout(() => this.siteSwitchTrigger.openMenu());
          }
        }
      });

    this.enrolledSites$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(sites => {
        this.availableSites = sites;
        this.enrolledSites = sites;

        this.filterSearchText();
      });

    this.authService.getUserLogin$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(u => {
        this.districtUserType =  u && (u.isDistrictUser() || u.isDestinyAdmin());
        this.isGuest = u && u.isGuestUser();
        this.isShared = u && u.isSharedUser();
      });
  }

  public focus(): void {
    if (this.enrolledSiteCount > 10) {
      if (this.schoolFilterInput) {
        setTimeout(() => this.schoolFilterInput.nativeElement.focus());
      }
    } else {
      document.getElementById('selected-site')?.focus();
    }
  }

  public clearSearchText() {
    this.searchText = '';
    this.filterSearchText();
  }

  public filterSearchText() {
    this.filteredSites = this.enrolledSites.filter((site) =>
      site.guid !== this.currentSite?.guid &&
      site.siteName.toLowerCase().includes(this.searchText.toLowerCase())
    );
  }

  public isUnEnabledForCurrentApp() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }

  public hackToHandleSearchFocus(event: any) {
    //sometimes, when searching, the focus is lost from the input field.
    //this is to ensure that does not happen
    if (!event.sourceCapabilities) {
      this.schoolFilterInput.nativeElement.focus();
    }
  }

  public switchSiteTo(site: SiteModel) {
    this.toastService.dismiss();

    if (this.authService.isDistrictUserNotAtSite()) {
      this.router.navigate([], {relativeTo: this.route}); // clean up query params
    }
    this.siteSwitchTrigger.closeMenu();
    this.toolbarService.portal = undefined;
    this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, site.guid, 'switch-site-to'));
  }

  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }

  public showInSidenav() {
    if (this.isUnEnabledForCurrentApp() && this.isMobile) {
      this.portal = new TemplatePortal(this.siteSwitcherTemplate, this._viewContainerRef);
      this.toolbarService.portal = {templatePortal: this.portal, title: this.MOBILE_HEADER_TEXT};
    }
  }

  public showSelectAll() {
    if (this.districtUserType) {
      return !!this.authService.getSiteGuid() || this.portalStateService.getActiveApp()?.app !== Application.PORTAL;
    }
    return false;
  }

  public onSiteSwitcherOpen() {
    this.searchText = '';
    this.filterSearchText();
    this.focus();
  }

  public closeOldMobileSidenav() {
    this.toolbarService.closeSidenavOnMenuOpen();
  }

  public getButtonText() {
    const ulm: UserLoginModel = this.authService.getUserLogin();
    if (ulm) {
      if (this.districtUserType && !this.authService.getSiteGuid()) {
        return 'District View';
      } else {
        return this.availableSites.find(s => s.guid === ulm.getSiteGuid())?.siteName || ulm.getLocationName();
      }
    }
    return '';
  }

  public viewAllSites() {
    const queryParams: any = {
      appId: this.authService.getDistrictAppId()
    };

    if (this.authService.isDistrictUserAtSite() || this.authService.isDestinyAdminAtSite()) {
      if (this.portalStateService.getActiveApp().app === Application.DESTINY ||           
          this.portalStateService.getActiveApp().app === Application.CLM ||
          this.portalStateService.getActiveApp().app === Application.DASHBOARD) {
        queryParams.app = this.portalStateService.getActiveApp().app;
      }
    }

    this.router.navigate([], {queryParams: queryParams})
      .then(() => {
        this.siteSwitchTrigger.closeMenu();
        this.toolbarService.portal = undefined;
        if (this.authService.getSiteGuid() ) {
          this.authService.setSiteGuid(undefined);
          this.portalStateService.setActiveAppFromApplication(Application.PORTAL);
          this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, undefined));
        } else {
          this.portalStateService.setActiveAppFromApplication(Application.PORTAL);
        }
      });
  }
}
